/**
 * Single Object Delete Job Store — module store tracking the async
 * single-object-delete jobs currently in flight.
 *
 * Job bodies live in a `createRecordStore<ActiveSingleObjectDeleteJob>`
 * toolkit instance keyed by `String(jobId)` — the toolkit owns the record
 * map, listener bookkeeping, and notification mechanics (the
 * `agentFeedStore.ts` pattern). This module owns what the toolkit doesn't
 * know about: the insertion-ordered `jobIds` list backing the array
 * snapshot, and the org/session gate (`activeOrgId` + generation). Jobs are
 * keyed by `jobId` so two 202 deletes dispatched back-to-back each keep
 * their own banner and terminal handling — a second dispatch no longer
 * clobbers the first (which would strand the first job's rollback/success).
 * Jobs carry their `orgId` so the host can org-gate them and drop any left
 * over from a previous org when the active org changes.
 */

import { useSyncExternalStore } from 'react';

import { createRecordStore } from '@/lib/store/createRecordStore';

export interface ActiveSingleObjectDeleteJob {
  orgId: number;
  jobId: number;
  totalCount: number;
  entityTypeName: string;
  entityId: string;
  recordName: string;
  /**
   * The org/session generation captured when this delete was dispatched (see
   * {@link currentSingleObjectDeleteJobGeneration}). The banner re-checks it live
   * before applying any side effect (record restore, terminal toast, list
   * invalidation): if the generation has advanced, an org switch/logout crossed a
   * session boundary while a poll/fetch was in flight, so the response belongs to
   * a prior session — even in the same org after returning to it — and must not
   * touch the current one. Set by the store at accept time (it always equals the
   * live generation then), so callers pass it via the `dispatchGeneration`
   * argument, not on the job object.
   */
  dispatchGeneration: number;
}

/** The job shape a caller supplies; the store stamps {@link ActiveSingleObjectDeleteJob.dispatchGeneration}. */
export type NewSingleObjectDeleteJob = Omit<
  ActiveSingleObjectDeleteJob,
  'dispatchGeneration'
>;

const store = createRecordStore<ActiveSingleObjectDeleteJob>();

// Insertion-ordered jobIds. The toolkit holds bodies; this holds order, so the
// array snapshot can be rebuilt mid-batch via the batch-aware `store.getData`
// (the toolkit's Map snapshot is not batch-aware).
let jobIds: number[] = [];
// The currently-active org, mirrored from the host. Used to drop a late 202 that
// resolves AFTER an org switch/logout: the org-boundary cleanup already ran, so
// without this a stale job would be tracked and reappear when the user returns
// to that org. null = no active org (logged out).
let activeOrgId: number | null = null;
// Monotonic org/session generation, bumped on EVERY org-lifecycle transition
// (setActiveSingleObjectDeleteJobOrg — switch or logout). A delete captures the
// generation at dispatch; if it has advanced by the time the 202 resolves, an
// org boundary was crossed in between, so the response belongs to a PRIOR
// session and must be rejected — even when the user has returned to the SAME
// org (the org id alone can't tell this session from a previous one, which is
// how a stale banner/optimistic-record could otherwise resurrect). Only reset by
// the full test-teardown reset; never reset on a production transition, so a
// captured generation can never coincidentally match a later session.
let activeOrgGeneration = 0;
// Cached array snapshot. useSyncExternalStore requires getSnapshot to return a
// stable reference between changes, so we rebuild this only on mutation —
// before endBatch(), so it is already fresh when the toolkit notifies.
let snapshot: readonly ActiveSingleObjectDeleteJob[] = [];

/**
 * Rebuild the cached snapshot array from `jobIds` + the toolkit's bodies.
 * Called before `endBatch()` so subscribers re-reading `getSnapshot()`
 * synchronously inside the notification see the new data.
 */
function rebuildSnapshot(): void {
  const next: ActiveSingleObjectDeleteJob[] = [];
  for (const jobId of jobIds) {
    const data = store.getData(String(jobId));
    if (data) {
      next.push(data);
    }
  }
  snapshot = next;
}

/** Commit a job body to the toolkit. Jobs have no seq/fetch semantics — meta is inert. */
function commitJob(job: ActiveSingleObjectDeleteJob): void {
  store.commit(String(job.jobId), {
    data: job,
    meta: { lastSeq: 0, fetchedAt: 0, isComplete: true },
  });
}

/** Remove the given jobIds inside one batch. No-op when `ids` is empty. */
function removeJobs(ids: readonly number[]): void {
  if (ids.length === 0) {
    return;
  }
  store.beginBatch();
  for (const jobId of ids) {
    store.remove(String(jobId));
  }
  jobIds = jobIds.filter(jobId => !ids.includes(jobId));
  rebuildSnapshot();
  store.endBatch();
}

function clearTrackedJobs(): void {
  removeJobs(jobIds);
}

/**
 * The current org/session generation. Capture this at delete DISPATCH time (before
 * awaiting the delete request) and pass the captured value to
 * {@link startSingleObjectDeleteJob} when the 202 resolves.
 */
export function currentSingleObjectDeleteJobGeneration(): number {
  return activeOrgGeneration;
}

/**
 * Start tracking a newly-dispatched single-object-delete job.
 *
 * `dispatchGeneration` is the generation captured (via
 * {@link currentSingleObjectDeleteJobGeneration}) when the delete was dispatched.
 * The job is rejected when the org/session boundary has moved since then, so a
 * late 202 from a prior session — including one for the same org the user has
 * since returned to — can never resurrect a stale banner or optimistic record.
 * Any start while no org is active is also rejected.
 */
export function startSingleObjectDeleteJob(
  job: NewSingleObjectDeleteJob,
  dispatchGeneration: number
): void {
  if (activeOrgId === null || dispatchGeneration !== activeOrgGeneration) {
    return;
  }
  // Defensive: the generation check already implies the org matches, but drop a
  // job whose org isn't the active one rather than tracking a mismatch.
  if (job.orgId !== activeOrgId) {
    return;
  }
  store.beginBatch();
  if (!jobIds.includes(job.jobId)) {
    jobIds = [...jobIds, job.jobId];
  }
  // Stamp the accepted generation onto the tracked job so the banner can later
  // re-check it live and reject side effects from a superseded session.
  commitJob({ ...job, dispatchGeneration });
  rebuildSnapshot();
  store.endBatch();
}

/** Stop tracking a single-object-delete job once it has terminated. */
export function clearSingleObjectDeleteJob(jobId: number): void {
  if (!jobIds.includes(jobId)) {
    return;
  }
  removeJobs([jobId]);
}

/**
 * Drop any tracked jobs that don't belong to `orgId`. Called at the org
 * lifecycle boundary so a delete pending in the previous org stops polling and
 * can't restore old-org data into the shared CRM store from the new org.
 */
export function clearSingleObjectDeleteJobsForOtherOrgs(orgId: number): void {
  removeJobs(
    jobIds.filter(jobId => store.getData(String(jobId))?.orgId !== orgId)
  );
}

/**
 * Full reset (primarily for test teardown): clears tracked jobs AND resets the
 * org/session generation to 0. Never call this on a production org transition —
 * resetting the generation would let a captured generation coincidentally match a
 * later session; use {@link setActiveSingleObjectDeleteJobOrg} for switch/logout.
 */
export function clearAllSingleObjectDeleteJobs(): void {
  activeOrgId = null;
  activeOrgGeneration = 0;
  clearTrackedJobs();
}

/**
 * Mirror the active org into the store at the org lifecycle boundary. Pass the
 * current org id on a switch, or `null` on logout / no active org. Every call
 * advances the org/session generation, so any delete dispatched before it is
 * rejected when its 202 resolves — this is what stops a late response from a
 * prior session (even in the same org, after returning to it) being tracked.
 *
 * - A real org id drops any tracked job that belongs to a different org, and
 *   makes {@link startSingleObjectDeleteJob} ignore a late job for another org
 *   or a prior generation.
 * - `null` clears ALL tracked jobs, so a later session (a different user in the
 *   same org, after logout) can't reattach to a prior user's stale job.
 */
export function setActiveSingleObjectDeleteJobOrg(orgId: number | null): void {
  activeOrgGeneration += 1;
  if (orgId === null) {
    activeOrgId = null;
    clearTrackedJobs();
    return;
  }
  activeOrgId = orgId;
  clearSingleObjectDeleteJobsForOtherOrgs(orgId);
}

function subscribe(listener: () => void): () => void {
  return store.subscribe(listener);
}

function getSnapshot(): readonly ActiveSingleObjectDeleteJob[] {
  return snapshot;
}

/** React hook for the in-flight single-object-delete jobs (empty when none). */
export function useActiveSingleObjectDeleteJobs(): readonly ActiveSingleObjectDeleteJob[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
